import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Search, Building2, MapPin, Clock, Users, X, Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyVisits = () => {
  const [visits, setVisits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    company_id: '',
    visit_date: '',
    visit_time: '',
    venue: '',
    purpose: 'Campus Recruitment',
    expected_roles: [],
    eligible_courses: [],
    coordinator: '',
    coordinator_phone: '',
    notes: ''
  });
  const [roleInput, setRoleInput] = useState('');
  const token = localStorage.getItem('token');

  const purposes = ['Campus Recruitment', 'Pool Campus', 'Internship Drive', 'Pre-Placement Talk', 'Workshop', 'Other'];

  useEffect(() => {
    fetchVisits();
    fetchCompanies();
    fetchCourses();
  }, [statusFilter]);

  const fetchVisits = async () => {
    try {
      let url = `${API_URL}/api/placement/visits`;
      if (statusFilter) url += `?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisits(response.data);
    } catch (error) {
      console.error('Error fetching visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/placement/companies/${formData.company_id}/visits`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Visit scheduled successfully');
      setShowModal(false);
      resetForm();
      fetchVisits();
    } catch (error) {
      console.error('Error scheduling visit:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule visit');
    }
  };

  const updateVisitStatus = async (visitId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/placement/visits/${visitId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Visit marked as ${newStatus}`);
      fetchVisits();
    } catch (error) {
      console.error('Error updating visit:', error);
      toast.error('Failed to update visit status');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      visit_date: '',
      visit_time: '',
      venue: '',
      purpose: 'Campus Recruitment',
      expected_roles: [],
      eligible_courses: [],
      coordinator: '',
      coordinator_phone: '',
      notes: ''
    });
    setRoleInput('');
  };

  const addRole = () => {
    if (roleInput.trim() && !formData.expected_roles.includes(roleInput.trim())) {
      setFormData({
        ...formData,
        expected_roles: [...formData.expected_roles, roleInput.trim()]
      });
      setRoleInput('');
    }
  };

  const removeRole = (role) => {
    setFormData({
      ...formData,
      expected_roles: formData.expected_roles.filter(r => r !== role)
    });
  };

  const toggleCourse = (courseName) => {
    const courses = formData.eligible_courses.includes(courseName)
      ? formData.eligible_courses.filter(c => c !== courseName)
      : [...formData.eligible_courses, courseName];
    setFormData({ ...formData, eligible_courses: courses });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isUpcoming = (dateStr) => {
    return new Date(dateStr) >= new Date(new Date().toDateString());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-visits-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Visits</h1>
          <p className="text-sm text-gray-500 mt-1">Schedule and manage company campus visits</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Schedule Visit
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-2">
          {['', 'scheduled', 'completed', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                statusFilter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Visits List */}
      <div className="space-y-4">
        {visits.length > 0 ? (
          visits.map(visit => (
            <div key={visit.id} className={`bg-white rounded-xl shadow-sm border p-6 ${
              isUpcoming(visit.visit_date) && visit.status === 'scheduled' ? 'border-l-4 border-l-blue-500' : ''
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{visit.company_name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusStyle(visit.status)}`}>
                      {visit.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{visit.purpose}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {visit.visit_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {visit.visit_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {visit.venue}
                    </span>
                    {visit.coordinator && (
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {visit.coordinator} ({visit.coordinator_phone})
                      </span>
                    )}
                  </div>

                  {visit.expected_roles?.length > 0 && (
                    <div className="mt-3">
                      <span className="text-xs text-gray-500">Roles: </span>
                      {visit.expected_roles.map(role => (
                        <span key={role} className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs mr-1">
                          {role}
                        </span>
                      ))}
                    </div>
                  )}

                  {visit.eligible_courses?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Eligible: </span>
                      {visit.eligible_courses.map(course => (
                        <span key={course} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs mr-1">
                          {course}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {visit.status === 'scheduled' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateVisitStatus(visit.id, 'completed')}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Mark as Completed"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => updateVisitStatus(visit.id, 'cancelled')}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Cancel Visit"
                    >
                      <XCircle className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No company visits scheduled</p>
          </div>
        )}
      </div>

      {/* Schedule Visit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Schedule Company Visit</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <select
                    required
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Company</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label>
                  <select
                    required
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {purposes.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visit Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.visit_date}
                    onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visit Time *</label>
                  <input
                    type="time"
                    required
                    value={formData.visit_time}
                    onChange={(e) => setFormData({ ...formData, visit_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue *</label>
                  <input
                    type="text"
                    required
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Seminar Hall"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coordinator Name</label>
                  <input
                    type="text"
                    value={formData.coordinator}
                    onChange={(e) => setFormData({ ...formData, coordinator: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coordinator Phone</label>
                  <input
                    type="tel"
                    value={formData.coordinator_phone}
                    onChange={(e) => setFormData({ ...formData, coordinator_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected Roles</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRole())}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Software Engineer"
                  />
                  <button type="button" onClick={addRole} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.expected_roles.map(role => (
                    <span key={role} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1">
                      {role}
                      <button type="button" onClick={() => removeRole(role)}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Eligible Courses</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                  {courses.map(course => (
                    <button
                      key={course.id}
                      type="button"
                      onClick={() => toggleCourse(course.name)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        formData.eligible_courses.includes(course.name)
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {course.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Schedule Visit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyVisits;
